import React, { useRef, useEffect } from 'react';
import { TraceStep } from '../types';
import { motion, AnimatePresence } from 'framer-motion';
import { Terminal, ChevronRight, ChevronLeft, Search } from 'lucide-react';
import { cn } from '../lib/utils';

interface OpcodeStepperProps {
  steps: TraceStep[];
  currentStepIndex: number;
  onStepChange: (index: number) => void;
}

export function OpcodeStepper({ steps, currentStepIndex, onStepChange }: OpcodeStepperProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const currentStep = steps[currentStepIndex];

  useEffect(() => {
    const activeElement = scrollRef.current?.querySelector(`[data-index="${currentStepIndex}"]`);
    if (activeElement) {
      activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentStepIndex]);

  if (!currentStep) {
    return (
      <div className="w-full h-full flex flex-col bg-[#0A0A0A] items-center justify-center p-8 text-center">
        <Terminal className="w-8 h-8 text-[#262626] mb-4" />
        <p className="text-[10px] text-[#525252] font-mono uppercase tracking-widest">
          No opcode trace available for this transaction.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-[#0A0A0A] overflow-hidden">
      <div className="h-10 border-b border-[#262626] flex items-center px-4 justify-between bg-[#0F0F0F] shrink-0">
        <div className="flex items-center gap-2">
          <Terminal className="w-4 h-4 text-emerald-500" />
          <h2 className="text-[11px] font-bold uppercase tracking-widest text-white">Opcode Trace</h2>
        </div>
        <div className="flex items-center gap-2 md:gap-4">
          <div className="flex items-center gap-1">
            <button 
              onClick={() => onStepChange(Math.max(0, currentStepIndex - 1))}
              className="p-1 hover:bg-white/5 rounded transition-colors text-[#525252] hover:text-white"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="font-mono text-[9px] md:text-[10px] text-[#A3A3A3] min-w-[40px] md:min-w-[60px] text-center">
              {currentStepIndex + 1} / {steps.length}
            </div>
            <button 
              onClick={() => onStepChange(Math.min(steps.length - 1, currentStepIndex + 1))}
              className="p-1 hover:bg-white/5 rounded transition-colors text-[#525252] hover:text-white"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="hidden sm:block h-4 w-[1px] bg-[#262626]" />
          <div className="hidden sm:relative sm:block">
            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-[#525252]" />
            <input 
              type="text" 
              placeholder="JUMPTO..."
              className="bg-[#1A1A1A] border border-[#262626] rounded px-6 py-1 text-[9px] font-mono text-white placeholder:text-[#404040] focus:outline-none focus:border-emerald-500/50 w-24"
            />
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Opcode List */}
        <div 
          ref={scrollRef}
          className="w-full md:w-1/2 h-1/2 md:h-full overflow-y-auto custom-scrollbar border-b md:border-b-0 md:border-r border-[#262626] py-2"
        >
          {steps.map((step, i) => (
            <div 
              key={`${step.pc}-${i}`}
              data-index={i}
              onClick={() => onStepChange(i)}
              className={cn(
                "group flex items-center gap-4 py-1.5 px-4 cursor-pointer transition-all border-l-2",
                i === currentStepIndex 
                  ? "bg-emerald-500/10 border-emerald-500 text-white" 
                  : "border-transparent text-[#525252] hover:bg-white/5 hover:text-[#A3A3A3]"
              )}
            >
              <span className="font-mono text-[10px] w-8 text-right opacity-50">{step.pc}</span>
              <span className={cn(
                "font-mono text-[10px] font-bold uppercase",
                i === currentStepIndex ? "text-emerald-400" : "text-[#A3A3A3]"
              )}>
                {step.op}
              </span>
              <span className="ml-auto font-mono text-[9px] opacity-0 group-hover:opacity-100 transition-opacity">
                GAS: {step.gas}
              </span>
            </div>
          ))}
        </div>

        {/* Stack & Memory Inspector */}
        <div className="w-full md:w-1/2 h-1/2 md:h-full overflow-y-auto custom-scrollbar bg-[#0F0F0F] p-4 flex flex-col gap-6">
          <section>
            <h3 className="text-[9px] font-bold uppercase tracking-widest text-[#525252] mb-3 flex items-center gap-2">
              <div className="w-1 h-1 rounded-full bg-emerald-500" />
              Stack
            </h3>
            <div className="flex flex-col gap-1">
              {currentStep.stack.length > 0 ? (
                currentStep.stack.map((val, i) => (
                  <div key={i} className="flex gap-3 font-mono text-[10px]">
                    <span className="text-[#404040] w-4">[{currentStep.stack.length - 1 - i}]</span>
                    <span className="text-[#A3A3A3] break-all">{val}</span>
                  </div>
                ))
              ) : (
                <span className="font-mono text-[10px] text-[#404040] italic">EMPTY</span>
              )}
            </div>
          </section>

          <section>
            <h3 className="text-[9px] font-bold uppercase tracking-widest text-[#525252] mb-3 flex items-center gap-2">
              <div className="w-1 h-1 rounded-full bg-blue-500" />
              Memory
            </h3>
            <div className="font-mono text-[10px] text-[#A3A3A3] leading-relaxed break-all">
              {currentStep.memory?.join('') || '0x'}
            </div>
          </section>

          <section>
            <h3 className="text-[9px] font-bold uppercase tracking-widest text-[#525252] mb-3 flex items-center gap-2">
              <div className="w-1 h-1 rounded-full bg-purple-500" />
              Storage Context
            </h3>
            <div className="flex flex-col gap-2">
              {currentStep.storage && Object.keys(currentStep.storage).length > 0 ? (
                Object.entries(currentStep.storage).map(([slot, val]) => (
                  <div key={slot} className="flex flex-col gap-1 bg-[#1A1A1A] p-2 rounded border border-[#262626]">
                    <span className="text-[9px] text-[#525252] font-mono">SLOT: {slot}</span>
                    <span className="text-[10px] text-white font-mono break-all">{val}</span>
                  </div>
                ))
              ) : (
                <span className="font-mono text-[10px] text-[#404040] italic">NO STORAGE ACCESS</span>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
